import { useState, useEffect, useCallback } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { TbX, TbList, TbTag, TbClock, TbMapPin } from "react-icons/tb";
import type { TemplateTask, TemplateStep } from "@/types/task";
import { Button } from "@/components/ui/Button";
import { TaskService } from "@/services/taskService";
import { useAllUsers } from "@/hooks/useUsers";
import { useTeams } from "@/hooks/useTeams";
import { ensureProtocol } from "@/lib/linkify";

interface TemplateViewModalProps {
  template: TemplateTask | null;
  open: boolean;
  onClose: () => void;
}

export const TemplateViewModal: React.FC<TemplateViewModalProps> = ({
  template,
  open,
  onClose,
}) => {
  const [templateSteps, setTemplateSteps] = useState<TemplateStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use React Query hooks for users and teams
  const { data: users = [], isLoading: usersTeamsLoading } = useAllUsers();
  const { data: teams = [] } = useTeams();

  const loadTemplateSteps = useCallback(async () => {
    if (!template) return;

    setLoading(true);
    setError(null);

    try {
      // First try to use template_steps from the passed template
      if (template.template_steps && template.template_steps.length > 0) {
        setTemplateSteps(template.template_steps);
        setLoading(false);
        return;
      }

      // If no steps in the passed template, fetch from database
      const templates = await TaskService.getTemplates();
      const fullTemplate = templates.find((t) => t.id === template.id);

      if (fullTemplate && fullTemplate.template_steps) {
        setTemplateSteps(fullTemplate.template_steps);
      } else {
        setTemplateSteps([]);
      }
    } catch (err: unknown) {
      console.error("Error loading template steps:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load template steps";
      setError(errorMessage);
      setTemplateSteps([]);
    } finally {
      setLoading(false);
    }
  }, [template]);

  useEffect(() => {
    if (template && open) {
      loadTemplateSteps();
    } else if (!open) {
      // Reset state when modal closes
      setTemplateSteps([]);
      setError(null);
      setLoading(false);
    }
  }, [template, open, loadTemplateSteps]);

  // Helper function to get assignee display name
  const getAssigneeDisplayName = (assigneeId: string, assigneeType: string) => {
    if (!assigneeId || usersTeamsLoading) return assigneeId || "Not set";

    if (assigneeType === "User") {
      const user = users.find((u) => u.id === assigneeId);
      if (user) {
        return user.first_name || user.last_name
          ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
          : user.email;
      }
    } else if (assigneeType === "Team") {
      const team = teams.find((t) => t.id === assigneeId);
      if (team) {
        return team.name;
      }
    }

    return assigneeId; // Fallback to ID if not found
  };

  if (!template) {
    return null;
  }

  return (
    <Dialog.Root open={open} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-2010" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg max-w-4xl w-[90vw] max-h-[90vh] overflow-hidden z-2011">
          <div className="flex items-center justify-between p-6 border-b">
            <Dialog.Title className="text-xl font-semibold text-gray-900">
              Template Details
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="outline" size="sm" className="p-2">
                <TbX size={16} />
              </Button>
            </Dialog.Close>
          </div>

          <Dialog.Description className="sr-only">
            View complete template information including basic details and all
            template steps
          </Dialog.Description>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {/* Template Basic Info */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {template.name}
              </h2>

              {template.description && (
                <p className="text-gray-600 mb-4">{template.description}</p>
              )}

              {/* Template Metadata Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {template.layer_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <TbMapPin className="text-blue-500" size={16} />
                    <span className="font-medium">Layer:</span>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                      {template.layer_name}
                    </span>
                  </div>
                )}

                {template.category && (
                  <div className="flex items-center gap-2 text-sm">
                    <TbTag className="text-green-500" size={16} />
                    <span className="font-medium">Category:</span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                      {template.category.label}
                    </span>
                  </div>
                )}

                {template.priority && (
                  <div className="flex items-center gap-2 text-sm">
                    <TbClock className="text-orange-500" size={16} />
                    <span className="font-medium">Priority:</span>
                    <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">
                      {template.priority.label}
                    </span>
                  </div>
                )}

                {template.status && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">Status:</span>
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                      {template.status.label}
                    </span>
                  </div>
                )}

                {template.default_assignee_type && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">Assignee Type:</span>
                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">
                      {template.default_assignee_type}
                    </span>
                  </div>
                )}

                {template.default_assignee && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">Assignee:</span>
                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">
                      {getAssigneeDisplayName(
                        template.default_assignee,
                        template.default_assignee_type || ""
                      )}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">Created:</span>
                  <span className="text-gray-600">
                    {new Date(template.created_at).toLocaleString()}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">Updated:</span>
                  <span className="text-gray-600">
                    {new Date(template.updated_at).toLocaleString()}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">Template ID:</span>
                  <span className="text-gray-600 font-mono text-xs">
                    {template.id}
                  </span>
                </div>
              </div>

              {/* Additional Template Fields */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  Additional Template Settings
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  {template.default_start_days && (
                    <div>
                      <span className="font-medium text-gray-700">
                        Start Days:
                      </span>
                      <span className="ml-2 text-gray-600">
                        {template.default_start_days} days
                      </span>
                    </div>
                  )}
                  {template.default_due_days && (
                    <div>
                      <span className="font-medium text-gray-700">
                        Due Days:
                      </span>
                      <span className="ml-2 text-gray-600">
                        {template.default_due_days} days
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-gray-700">Active:</span>
                    <span
                      className={`ml-2 px-2 py-1 rounded text-xs ${
                        template.is_active
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {template.is_active ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Template Steps */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <TbList className="text-gray-600" size={20} />
                <h3 className="text-lg font-semibold text-gray-900">
                  Template Steps
                </h3>
                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">
                  {templateSteps.length} step
                  {templateSteps.length !== 1 ? "s" : ""}
                </span>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-500">
                  Loading template steps...
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-500">{error}</div>
              ) : templateSteps.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No steps defined for this template.
                </div>
              ) : (
                <div className="space-y-4">
                  {templateSteps
                    .sort((a, b) => a.step_order - b.step_order)
                    .map((step, index) => (
                      <div
                        key={step.id}
                        className="border rounded-lg p-5 bg-white shadow-sm"
                      >
                        <div className="flex items-start gap-4">
                          <div className="shrink-0 w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                Sequence: {step.sequence}
                              </span>
                            </div>

                            <p className="text-sm font-medium text-gray-700 mb-2">
                              Step Name:{" "}
                              <span className="font-normal">
                                {step.step_name}
                              </span>
                            </p>

                            {step.description && (
                              <p className="text-sm text-gray-600 mb-3">
                                <span className="font-medium">
                                  Description:
                                </span>{" "}
                                {step.description}
                              </p>
                            )}

                            {/* Step Assignment Info */}
                            <div className="bg-gray-50 rounded-lg p-3 mb-3">
                              <h5 className="font-medium text-gray-800 mb-2">
                                Assignment Details
                              </h5>
                              <div className="grid grid-cols-1 gap-3 text-sm">
                                <div>
                                  <span className="font-medium text-gray-700">
                                    Assignee Type:
                                  </span>
                                  <span
                                    className={`ml-2 px-2 py-1 rounded text-xs ${
                                      step.default_assignee_type === "User"
                                        ? "bg-green-100 text-green-800"
                                        : step.default_assignee_type === "Team"
                                          ? "bg-blue-100 text-blue-800"
                                          : "bg-gray-100 text-gray-600"
                                    }`}
                                  >
                                    {step.default_assignee_type || "Not set"}
                                  </span>
                                </div>
                                <div>
                                  <span className="font-medium text-gray-700">
                                    Assignee:
                                  </span>
                                  <span className="ml-2 px-2 py-1 bg-white border rounded text-xs">
                                    {getAssigneeDisplayName(
                                      step.default_assignee || "",
                                      step.default_assignee_type || ""
                                    )}
                                  </span>
                                  {/* {step.default_assignee && (
                                    <div className="mt-1">
                                      <span className="text-xs text-gray-500">
                                        ID:{" "}
                                      </span>
                                      <span className="text-xs text-gray-500 font-mono">
                                        {step.default_assignee}
                                      </span>
                                    </div>
                                  )} */}
                                </div>
                              </div>
                            </div>

                            {/* Step Settings */}
                            <div className="bg-gray-50 rounded-lg p-3 mb-3">
                              <h5 className="font-medium text-gray-800 mb-2">
                                Step Settings
                              </h5>
                              <div className="flex flex-wrap gap-2">
                                {step.is_required && (
                                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                                    Required
                                  </span>
                                )}
                                {step.auto_complete && (
                                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                                    Auto Complete
                                  </span>
                                )}
                                {step.requires_photos && (
                                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                    Requires Photos
                                  </span>
                                )}
                                {step.requires_notes && (
                                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                                    Requires Notes
                                  </span>
                                )}
                                {step.notification_on_completion && (
                                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                    Notify on Completion
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Step Links */}
                            {(step.reference_link || step.survey_link) && (
                              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                <h5 className="font-medium text-gray-800 mb-2">
                                  Links
                                </h5>
                                <div className="space-y-2 text-sm">
                                  {step.reference_link && (
                                    <div>
                                      <span className="font-medium text-gray-700">
                                        Reference:
                                      </span>
                                      <a
                                        href={ensureProtocol(
                                          step.reference_link
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-2 text-blue-600 hover:text-blue-800 underline break-all"
                                      >
                                        {step.reference_link}
                                      </a>
                                    </div>
                                  )}
                                  {step.survey_link && (
                                    <div>
                                      <span className="font-medium text-gray-700">
                                        Survey:
                                      </span>
                                      <a
                                        href={ensureProtocol(step.survey_link)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-2 text-blue-600 hover:text-blue-800 underline break-all"
                                      >
                                        {step.survey_link}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Additional Step Details */}
                            <div className="bg-gray-50 rounded-lg p-3">
                              <h5 className="font-medium text-gray-800 mb-2">
                                Additional Details
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                                <div>
                                  <span className="font-medium text-gray-700">
                                    Step Order:
                                  </span>
                                  <span className="ml-2 text-gray-600">
                                    {step.step_order}
                                  </span>
                                </div>

                                {step.reminder_days_before && (
                                  <div>
                                    <span className="font-medium text-gray-700">
                                      Reminder Days:
                                    </span>
                                    <span className="ml-2 text-gray-600">
                                      {step.reminder_days_before} days
                                    </span>
                                  </div>
                                )}

                                {step.escalation_days && (
                                  <div>
                                    <span className="font-medium text-gray-700">
                                      Escalation Days:
                                    </span>
                                    <span className="ml-2 text-gray-600">
                                      {step.escalation_days} days
                                    </span>
                                  </div>
                                )}

                                <div>
                                  <span className="font-medium text-gray-700">
                                    Step ID:
                                  </span>
                                  <span className="ml-2 text-gray-600 font-mono text-xs">
                                    {step.id}
                                  </span>
                                </div>

                                <div>
                                  <span className="font-medium text-gray-700">
                                    Created:
                                  </span>
                                  <span className="ml-2 text-gray-600">
                                    {new Date(step.created_at).toLocaleString()}
                                  </span>
                                </div>

                                <div>
                                  <span className="font-medium text-gray-700">
                                    Updated:
                                  </span>
                                  <span className="ml-2 text-gray-600">
                                    {new Date(step.updated_at).toLocaleString()}
                                  </span>
                                </div>
                              </div>

                              {step.completion_criteria && (
                                <div className="mt-3">
                                  <span className="font-medium text-gray-700">
                                    Completion Criteria:
                                  </span>
                                  <p className="mt-1 text-gray-600 text-sm bg-white p-2 rounded border">
                                    {step.completion_criteria}
                                  </p>
                                </div>
                              )}

                              {step.depends_on_step_ids &&
                                step.depends_on_step_ids.length > 0 && (
                                  <div className="mt-3">
                                    <span className="font-medium text-gray-700">
                                      Dependencies:
                                    </span>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {step.depends_on_step_ids.map(
                                        (depId, idx) => (
                                          <span
                                            key={idx}
                                            className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-mono"
                                          >
                                            {depId}
                                          </span>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
